import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { useAuth } from '../../auth';
import { route } from '../../../shared/utils';

const GOLD = '#FFD93D';

type Layout = { preset: string; title: string; sub: string };

// Real captured render of each preset (cropped plot figure) as the thumbnail,
// using the irregular capture when the irregular shape is selected.
const shot = (name: string, shape: 'regular' | 'irregular') => {
  const file = `${name}${shape === 'irregular' ? '_irr' : ''}`;
  return <img src={`/thumbnails/${file}.svg`} alt="" style={{ width: '100%', display: 'block', background: '#fff' }} />;
};

const LAYOUTS: Layout[] = [
  { preset: 'none', title: 'Just the plot', sub: 'Empty boundary' },
  { preset: 'single', title: 'One Room', sub: '10×10 · door · dims' },
  { preset: 'double', title: 'Two Rooms', sub: 'Two 10×10, shared wall' },
  { preset: 'houseStairs', title: 'House + Stairs', sub: 'Living · stairs · bedroom' },
  { preset: 'house1bhk', title: '1 BHK House', sub: 'Bed, kitchen, drawing room, 2 toilets' },
  { preset: 'house2bhk', title: '2 BHK House', sub: '2 beds, 2 toilets, hall, kitchen, drawing' },
  { preset: 'house2bhkB', title: '2 BHK — Puja + L-Hall', sub: 'Kitchen, puja, L-hall, 2 beds, 2 toilets' },
  { preset: 'house3bhk', title: '3 BHK House', sub: '3 beds, L-hall, kitchen, 3 toilets' },
];

const NewPlotPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [shape, setShape] = useState<'regular' | 'irregular'>('regular');

  if (!user) return null;

  const start = (preset: string) => navigate(route(`/services/flex?service=plot_${shape}&content=sample&preset=${preset}`));

  return (
    <div style={{ minHeight: '100vh', background: '#0f1011', color: '#eceef0', fontFamily: 'ui-sans-serif, system-ui, sans-serif' }}>
      <SEO title="New Plot — RRD Planners" description="Choose a plot shape and layout to start a new design" />
      <div style={{ maxWidth: 920, margin: '0 auto', padding: '28px 20px 64px' }}>
        <button onClick={() => navigate(route('/dashboard'))}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#9aa0a6', cursor: 'pointer', padding: '6px 0', fontSize: 13 }}>
          <ArrowLeft size={16} /> Back to dashboard
        </button>

        <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', margin: '10px 0 6px' }}>Start a new plot</h1>
        <p style={{ color: '#9aa0a6', fontSize: 15, margin: 0 }}>Pick a plot shape and a layout — everything is editable afterwards.</p>

        {/* Plot shape */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 22 }}>
          <span style={{ color: '#9aa0a6', fontSize: 13 }}>Plot shape</span>
          <div style={{ display: 'flex', gap: 3, background: '#1a1b1d', borderRadius: 9, padding: 3, border: '1px solid #2a2b2e' }}>
            {(['regular', 'irregular'] as const).map((s) => (
              <button key={s} onClick={() => setShape(s)}
                style={{ background: shape === s ? GOLD : 'transparent', color: shape === s ? '#1a1a1a' : '#bbb', border: 'none', borderRadius: 6, padding: '6px 16px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', textTransform: 'capitalize' }}>
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Layout cards */}
        <div style={{ color: '#9aa0a6', fontSize: 13, marginTop: 22, marginBottom: 10 }}>Choose a layout</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
          {LAYOUTS.map((l) => (
            <button key={l.preset} onClick={() => start(l.preset)} title={`Start: ${l.title}`}
              style={{ display: 'flex', flexDirection: 'column', textAlign: 'left', background: '#17181a', border: '1px solid #2a2b2e', borderRadius: 14, padding: 12, cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = GOLD; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#2a2b2e'; }}>
              <div style={{ background: '#0d0d0d', border: '1px solid #232323', borderRadius: 9, overflow: 'hidden', lineHeight: 0 }}>{shot(l.preset, shape)}</div>
              <span style={{ color: '#f3f4f6', fontSize: 14, fontWeight: 700, marginTop: 10 }}>{l.title}</span>
              <span style={{ color: '#8a9098', fontSize: 12, marginTop: 2 }}>{l.sub}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NewPlotPage;
